// assets/client-pagination.js
// 通用前端分页 Web Component

class ClientPagination extends HTMLElement {
  static get observedAttributes() {
    return ['total', 'page-size'];
  }

  constructor() {
    super();
    this._total = 0;
    this._pageSize = 10;
    this._current = 1;
  }

  connectedCallback() {
    this._total = parseInt(this.getAttribute('total')) || 0;
    this._pageSize = parseInt(this.getAttribute('page-size')) || 10;
    
    this.addEventListener('click', this.handleClick.bind(this));
    this.update();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    
    if (name === 'total') {
      this._total = parseInt(newValue) || 0;
      this._current = 1; // 重置到第一页
      this.update();
      this.dispatchPageChange();
    } else if (name === 'page-size') {
      this._pageSize = parseInt(newValue) || 10;
      this.update();
    }
  }

  // Getters/Setters
  get total() { return this._total; }
  set total(val) { this.setAttribute('total', val); }

  get current() { return this._current; }
  set current(val) {
    const totalPages = this.totalPages;
    const newVal = Math.max(1, Math.min(val, totalPages));
    if (newVal !== this._current) {
      this._current = newVal;
      this.update();
      this.dispatchPageChange();
    }
  }

  get pageSize() { return this._pageSize; }
  set pageSize(val) { this.setAttribute('page-size', val); }

  get totalPages() {
    return Math.ceil(this._total / this._pageSize) || 1;
  }

  handleClick(e) {
    const btn = e.target.closest('[data-page]');
    if (!btn || btn.disabled) return;
    
    e.preventDefault();
    const action = btn.dataset.page;
    
    if (action === 'prev') {
      this.current = this._current - 1;
    } else if (action === 'next') {
      this.current = this._current + 1;
    } else {
      const page = parseInt(action);
      if (page && page !== this._current) {
        this.current = page;
      }
    }
  }

  dispatchPageChange() {
    this.dispatchEvent(new CustomEvent('page-change', {
      bubbles: true,
      detail: { 
        page: this._current, 
        totalPages: this.totalPages,
        start: (this._current - 1) * this._pageSize,
        end: Math.min(this._current * this._pageSize, this._total)
      }
    }));
  }

  update() {
    const totalPages = this.totalPages;
    const current = this._current;
    
    // 如果没有数据或只有一页，隐藏整个组件
    if (this._total === 0 || totalPages <= 1) {
      this.style.display = 'none';
      return;
    }
    this.style.display = '';
    
    // 上一页按钮
    const prevBtn = this.querySelector('[data-page="prev"]');
    if (prevBtn) {
      prevBtn.style.display = current > 1 ? '' : 'none';
    }
    
    // 下一页按钮
    const nextBtn = this.querySelector('[data-page="next"]');
    if (nextBtn) {
      nextBtn.style.display = current < totalPages ? '' : 'none';
    }
    
    // 页码按钮：计算显示范围
    const pageButtons = this.querySelectorAll('.pagination-pages .pagination-btn');
    const maxVisible = pageButtons.length;
    
    if (maxVisible === 0) return;
    
    let start = Math.max(1, current - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    pageButtons.forEach((btn, index) => {
      const pageNum = start + index;
      
      if (pageNum <= totalPages) {
        btn.style.display = '';
        btn.textContent = pageNum;
        btn.dataset.page = pageNum;
        btn.disabled = pageNum === current;
        btn.classList.toggle('is-active', pageNum === current);
      } else {
        btn.style.display = 'none';
      }
    });
  }

  // 重置到第一页
  reset() {
    this._current = 1;
    this.update();
    this.dispatchPageChange();
  }
}

customElements.define('client-pagination', ClientPagination);

